import React, { useCallback, useState } from "react";
import "../Quiz/Quiz.css";
import "./ChartGame.css";
import "./FactPicker.css";
import "./ProductGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { AnswerResult } from "../Quiz/answer.ts";
import { factKey, recordCorrect, recordMiss } from "../Quiz/missedFacts.ts";
import { MissedFactsPractice } from "../Quiz/MissedFactsPractice.tsx";
import { track } from "../../Shared/analytics.ts";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { ProductGame, ProductGameResult, ProductMode } from "./ProductGame.tsx";
import { productGameEventParams } from "./gameEventParams.ts";
import { operationOfVariant, ProductVariant } from "./productGame.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

interface ProductGamePageProps {
    slug: string;
    operation: Operations;
}

const pickIndex = (n: number) => getRandomInt(0, n - 1);

const VARIANTS: { variant: ProductVariant; labelKey: "flashCards.productVariantProduct" | "flashCards.productVariantSum" }[] = [
    { variant: "product", labelKey: "flashCards.productVariantProduct" },
    { variant: "sum", labelKey: "flashCards.productVariantSum" },
];

/**
 * The Product Game (ADR 0055) in its keyword landing page: choose Product Game
 * or Sum Game (ADR 0098 §3) and an opponent, then play. A quiz route carrying
 * `game: "product"`. No settings panel - the clips only work for 1-9. The Sum
 * Game is the first choice on an addition route, the Product Game otherwise.
 */
export function ProductGamePage({ slug, operation }: ProductGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [variant, setVariant] = useState<ProductVariant>(operation === Operations.Addition ? "sum" : "product");
    const [mode, setMode] = useState<ProductMode | null>(null);
    const [finished, setFinished] = useState(false);
    const [runId, setRunId] = useState(0);
    const [missesVersion, setMissesVersion] = useState(0);

    function start(chosen: ProductMode) {
        setMode(chosen);
        setFinished(false);
        setRunId((id) => id + 1);
    }

    const handleAnswer = useCallback((front: string, result: AnswerResult) => {
        if (result === "correct") {
            recordCorrect(factKey(front));
        } else {
            recordMiss(factKey(front));
        }
        setMissesVersion((v) => v + 1);
    }, []);

    // The operation played, not the route's: a Sum Game on the product page is addition.
    const handleFinish = useCallback((result: ProductGameResult) => {
        setFinished(true);
        track("quiz_completed", productGameEventParams(operationOfVariant(result.variant), result));
    }, []);

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool productTool">
                <p className="chartIntro">
                    {variant === "sum" ? t("flashCards.productSumHowTo") : t("flashCards.productHowTo")}
                </p>
                {mode === null ? (
                    <div className="quizModePicker">
                        <p className="quizModePrompt">{t("flashCards.productVariantPrompt")}</p>
                        <div
                            className="factPicker productVariantPicker"
                            role="group"
                            aria-label={t("flashCards.productVariantPrompt")}
                        >
                            {VARIANTS.map((v) => (
                                <button
                                    key={v.variant}
                                    type="button"
                                    className={`factPickerButton${v.variant === variant ? " factPickerButton--on" : ""}`}
                                    aria-pressed={v.variant === variant}
                                    onClick={() => setVariant(v.variant)}
                                >
                                    <span className="productVariantName">{t(v.labelKey)}</span>
                                </button>
                            ))}
                        </div>
                        <p className="quizModePrompt">{t("flashCards.productModePrompt")}</p>
                        <div className="quizModeButtons">
                            <button type="button" onClick={() => start("computer")}>
                                {t("flashCards.productVsComputer")}
                            </button>
                            <button type="button" onClick={() => start("two")}>
                                {t("flashCards.productTwoPlayers")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <ProductGame
                            key={runId}
                            variant={variant}
                            mode={mode}
                            pick={pickIndex}
                            onAnswer={handleAnswer}
                            onFinish={handleFinish}
                        />
                        {finished && (
                            <div className="productAfter">
                                <button type="button" className="quizPlayAgain" onClick={() => start(mode)}>
                                    {t("flashCards.playAgain")}
                                </button>
                                <button type="button" className="quizChangeMode" onClick={() => setMode(null)}>
                                    {t("flashCards.productChangeMode")}
                                </button>
                            </div>
                        )}
                    </>
                )}
                <MissedFactsPractice refreshKey={missesVersion} />
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </>
    );
}

export default ProductGamePage;
